/**
 * BDL — BrainStorm Diagram Language — as TypeScript types. Mirrors
 * app/lib/bdl/model.dart; the Dart side is the source of truth and its
 * validator is the gate. This side only needs to draw what validated.
 */

export type Role = "structure" | "driver" | "medium" | "outcome";
export type Shape = "path" | "rect" | "circle" | "line" | "polygon" | "arc";
export type Verb = "translate" | "rotate" | "scale" | "flowAlong" | "fillLevel" | "revealAlong" | "pulse" | "toggle";
export type Curve = "linear" | "easeIn" | "easeOut" | "easeInOut";

export interface Geometry {
  x?: number; y?: number; w?: number; h?: number; rx?: number;
  cx?: number; cy?: number; r?: number;
  x1?: number; y1?: number; x2?: number; y2?: number;
  points?: [number, number][];
  closed?: boolean;
  start?: number; sweep?: number;
  d?: string;
}

export interface Part {
  id: string;
  role: Role;
  shape: Shape;
  geometry: Geometry;
  fill?: boolean;
  emphasis?: boolean;
  dashed?: boolean;
  z?: number;
}

export interface Label {
  text: string;
  anchor: [number, number];
  leader?: [number, number][];
}

export interface Track {
  target: string | string[];
  verb: Verb;
  from?: number | [number, number];
  to?: number | [number, number];
  start?: number;
  duration?: number;
  curve?: Curve;
  pivot?: [number, number];
  count?: number;
  cycles?: number;
}

export interface Timeline {
  duration: number;
  loop?: boolean;
  tracks: Track[];
}

export interface Figure {
  kind?: "mechanism" | "scene";
  viewBox?: [number, number];
  /** What this figure shows, drawn under the drawing inside the figure. */
  caption?: string;
  parts: Part[];
  labels?: Label[];
  timeline?: Timeline | null;
}

/** A chain's beat (philosophy/01): a label, a few spoken sentences, a figure. */
export interface Beat {
  role: "ask" | "reason" | "consequence" | "say";
  head: string;
  /** The one sentence the card shows large, above the body. Falls back to the head when absent. */
  line?: string;
  body: string;
  figureId: string;
}
export interface Chain { voice?: string; beats: Beat[] }

export interface Spread {
  id: string;
  kind: "invented" | "natural";
  topic: string;
  tags?: string[];
  difficulty?: number;
  card: { title: string; hook: string; figureId: string; summary?: string };
  chain: Chain;
  /** false: approved but held back, not published. */
  feed?: boolean;
  figures: Record<string, Figure>;
  sources?: { claim: string; url: string }[];
  review?: { status: string; reviewedBy?: string | null; reviewedAt?: string | null };
}

export const centroid = (g: Geometry): [number, number] => {
  if (g.cx !== undefined && g.cy !== undefined) return [g.cx, g.cy];
  if (g.x !== undefined && g.y !== undefined && g.w !== undefined && g.h !== undefined) return [g.x + g.w / 2, g.y + g.h / 2];
  if (g.x1 !== undefined) return [((g.x1 ?? 0) + (g.x2 ?? 0)) / 2, ((g.y1 ?? 0) + (g.y2 ?? 0)) / 2];
  if (g.points?.length) {
    let sx = 0, sy = 0;
    for (const [x, y] of g.points) { sx += x; sy += y; }
    return [sx / g.points.length, sy / g.points.length];
  }
  return [50, 50];
};
